/**
 * Global Tooltip System
 * ----------------------------------------------------------------------------
 * 1. Migrates native `title` attributes to `data-tooltip` so the premium
 *    styled tooltip is used everywhere and no browser default tooltip ever
 *    flashes (works for static, dynamic, and lazily-mounted DOM).
 * 2. Renders every tooltip as a SINGLE floating layer appended to <body>,
 *    positioned with `position: fixed`. Tooltips are therefore always on
 *    top (maximum z-index — above modals, panels, cards, tables) and are
 *    never clipped or hidden by ancestors that create overflow or stacking
 *    contexts (navbar, item lists, table edges, ...).
 * 3. Default placement is ABOVE the trigger (top). Per-element overrides
 *    still work via data-tooltip-pos="bottom|left|right". When there is no
 *    room above, the tooltip smart-flips below instead of clipping off the
 *    viewport.
 *
 * NOTE: tooltips are hover-only, so they stay suppressed on touch / small
 * screens (JS mirror of the CSS media query). data-tooltip remains in the
 * DOM, so hybrid devices / resizing get tooltips back automatically.
 */

/* ---------------------------------------------------------------- */
/* title -> data-tooltip migration                                  */
/* ---------------------------------------------------------------- */

function stashTitle(el, raw) {
  if (!el.hasAttribute('data-original-title')) el.setAttribute('data-original-title', raw);
  el.removeAttribute('title');
}

function migrateTitle(el) {
  if (!(el instanceof Element)) return;
  if (!el.hasAttribute('title')) return;

  const raw = el.getAttribute('title');
  if (raw == null) return;
  const text = raw.trim();
  // Remove empty titles regardless
  if (!text) {
    el.removeAttribute('title');
    return;
  }
  // If already has data-tooltip, just suppress native title
  // AND keep the styled tooltip in sync (e.g. Pin <-> Unpin toggles re-render
  // with a new `title`, but without this the old data-tooltip would stick).
  if (el.hasAttribute('data-tooltip')) {
    const prev = el.getAttribute('data-tooltip');
    stashTitle(el, raw);
    if (text && text !== prev) {
      el.setAttribute('data-tooltip', text);
      // Keep auto-generated a11y label in sync (only when it was derived
      // from the previous tooltip text — never override an explicit label).
      if (el.getAttribute('aria-label') === prev) {
        el.setAttribute('aria-label', text);
      }
      // If this trigger's tooltip is currently visible, update it live.
      if (typeof activeTrigger !== 'undefined' && activeTrigger === el && tooltipEl) {
        const pill = tooltipEl.querySelector('.mivent-tooltip__pill');
        if (pill) pill.textContent = text;
      }
    }
    return;
  }

  el.setAttribute('data-tooltip', text);
  if (!el.hasAttribute('data-original-title')) el.setAttribute('data-original-title', raw);
  // Crucial: remove native title to prevent browser tooltip
  el.removeAttribute('title');

  // If the element is icon-only, ensure aria-label for a11y (preserve semantics)
  if (!el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby')) {
    const visibleText = (el.textContent || '').trim();
    if (!visibleText) {
      el.setAttribute('aria-label', text);
    }
  }
}

function scanRoot(root = document) {
  const nodes = root.querySelectorAll ? root.querySelectorAll('[title]') : [];
  nodes.forEach(migrateTitle);
}

/* ---------------------------------------------------------------- */
/* Floating tooltip layer (body-level, fixed)                       */
/* ---------------------------------------------------------------- */

const SHOW_DELAY_HOVER = 120; // ms — avoids flicker on quick pass-through
const SHOW_DELAY_FOCUS = 0; // keyboard users get it instantly
const GAP = 10; // px between tooltip and trigger
const EDGE = 8; // min px from viewport edge

let tooltipEl = null;
let activeTrigger = null;
let showTimer = null;
let raf = 0;
let spaceCalib = null;

function ensureTooltip() {
  if (tooltipEl) return tooltipEl;
  tooltipEl = document.createElement('div');
  tooltipEl.className = 'mivent-tooltip';
  tooltipEl.setAttribute('role', 'tooltip');
  tooltipEl.setAttribute('aria-hidden', 'true');

  const pill = document.createElement('span');
  pill.className = 'mivent-tooltip__pill';
  const arrow = document.createElement('span');
  arrow.className = 'mivent-tooltip__arrow';

  tooltipEl.appendChild(pill);
  tooltipEl.appendChild(arrow);
  // Appended to <body> so the layer escapes every ancestor stacking/
  // overflow context and sits at the very top of the document.
  document.body.appendChild(tooltipEl);
  return tooltipEl;
}

function isTouchEnvironment() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(hover: none), (pointer: coarse), (max-width: 768px)').matches
  );
}

function getTriggerText(el) {
  const raw = el.getAttribute('data-tooltip');
  return raw == null ? '' : String(raw).trim();
}

function hideTooltip() {
  if (showTimer) {
    clearTimeout(showTimer);
    showTimer = null;
  }
  activeTrigger = null;
  if (tooltipEl) {
    tooltipEl.classList.remove('mivent-tooltip--visible');
    tooltipEl.setAttribute('aria-hidden', 'true');
  }
}

function scheduleShow(trigger, delay) {
  if (showTimer) {
    clearTimeout(showTimer);
    showTimer = null;
  }
  if (!trigger || !trigger.isConnected) {
    hideTooltip();
    return;
  }
  showTimer = setTimeout(() => {
    showTimer = null;
    presentTooltip(trigger);
  }, delay);
}

// Measure how CSS offset units map to getBoundingClientRect units for this
// document. Some browsers ignore `zoom` (html/ancestor ancestors) when
// reporting rects but do scale position:fixed offsets differently — this app
// globally uses `html { zoom: 0.8 }` on desktop, which made tooltips appear
// far from their trigger. We probe the mapping once (0px -> 100px) and then
// convert every placement through it; when nothing is distorted the mapping
// is 1:1 and this is a harmless no-op.
function calibrateSpace() {
  const tt = tooltipEl;
  const prevLeft = tt.style.left;
  const prevTop = tt.style.top;
  const prevTransform = tt.style.transform;
  // Neutralize the tooltip's own transform so only ancestor zoom is measured.
  tt.style.transform = 'none';
  tt.style.left = '0px';
  tt.style.top = '0px';
  const r0 = tt.getBoundingClientRect();
  tt.style.left = '100px';
  tt.style.top = '100px';
  const r1 = tt.getBoundingClientRect();
  tt.style.left = prevLeft;
  tt.style.top = prevTop;
  tt.style.transform = prevTransform;

  const xScale = (r1.left - r0.left) / 100;
  const yScale = (r1.top - r0.top) / 100;
  return {
    xScale: Number.isFinite(xScale) && xScale !== 0 ? xScale : 1,
    yScale: Number.isFinite(yScale) && yScale !== 0 ? yScale : 1,
    xBase: Number.isFinite(r0.left) ? r0.left : 0,
    yBase: Number.isFinite(r0.top) ? r0.top : 0,
  };
}

// Convert rect-space (getBoundingClientRect) coordinates into the CSS
// offset space that position: fixed consumes in this document.
function toOffsetSpace(left, top) {
  if (!spaceCalib) spaceCalib = calibrateSpace();
  const { xScale, yScale, xBase, yBase } = spaceCalib;
  return {
    left: (left - xBase) / xScale,
    top: (top - yBase) / yScale,
  };
}

// Compute where the tooltip goes. Default: above; flips below only when
// there is no room above. Explicit data-tooltip-pos is honored.
// All inputs/outputs here are in getBoundingClientRect (rect) space;
// onMouseOver/applyPlacement convert to CSS offset space when applied.
function computePlacement(trigger, rect, tw, th, vw, vh) {
  const clamp = (v, min, max) => Math.min(Math.max(v, min), Math.max(min, max));
  const explicit = (trigger.getAttribute('data-tooltip-pos') || '').trim();

  const centerX = clamp(rect.left + rect.width / 2 - tw / 2, EDGE, vw - tw - EDGE);
  const centerY = clamp(rect.top + rect.height / 2 - th / 2, EDGE, vh - th - EDGE);
  const fitsAbove = rect.top - th - GAP >= EDGE;
  const fitsBelow = rect.bottom + th + GAP <= vh - EDGE;

  if (explicit === 'left' || explicit === 'right') {
    const onRight = explicit === 'right';
    const x = onRight ? rect.right + GAP : rect.left - GAP - tw;
    return { pos: explicit, left: clamp(x, EDGE, vw - tw - EDGE), top: centerY };
  }

  if (explicit === 'bottom') {
    const pos = fitsBelow ? 'bottom' : 'top';
    return {
      pos,
      left: centerX,
      top: pos === 'bottom' ? rect.bottom + GAP : rect.top - th - GAP,
    };
  }

  // Default: above, smart-flip below when there is no room above.
  const pos = fitsAbove ? 'top' : fitsBelow ? 'bottom' : 'top';
  return {
    pos,
    left: centerX,
    top: pos === 'top' ? rect.top - th - GAP : rect.bottom + GAP,
  };
}

function applyPlacement(tt, pos, left, top) {
  const off = toOffsetSpace(left, top);
  tt.classList.remove(
    'mivent-tooltip--pos-top',
    'mivent-tooltip--pos-bottom',
    'mivent-tooltip--pos-left',
    'mivent-tooltip--pos-right'
  );
  tt.classList.add(`mivent-tooltip--pos-${pos}`);
  tt.style.left = `${Math.round(off.left)}px`;
  tt.style.top = `${Math.round(off.top)}px`;
}

function presentTooltip(trigger) {
  if (!trigger || !trigger.isConnected || isTouchEnvironment()) return;
  const text = getTriggerText(trigger);
  if (!text) return;

  const tt = ensureTooltip();
  tt.querySelector('.mivent-tooltip__pill').textContent = text;

  const rect = trigger.getBoundingClientRect();
  const vw = window.innerWidth || document.documentElement.clientWidth;
  const vh = window.innerHeight || document.documentElement.clientHeight;

  // Measure the pill via its own rect (same space as the trigger rect).
  // The hidden state applies a scale transform, so neutralize it for an
  // exact measurement (the visible state has transform: none anyway).
  const prevTransform = tt.style.transform;
  tt.style.transform = 'none';
  const tRect = tt.getBoundingClientRect();
  tt.style.transform = prevTransform;
  const placement = computePlacement(trigger, rect, tRect.width, tRect.height, vw, vh);
  applyPlacement(tt, placement.pos, placement.left, placement.top);

  activeTrigger = trigger;
  tt.classList.add('mivent-tooltip--visible');
  tt.setAttribute('aria-hidden', 'false');
}

// Keep the tooltip glued to its trigger while scrolling (capture phase also
// catches scrolls inside nested containers like tables / lists).
function handleScroll() {
  if (!activeTrigger || !tooltipEl || !tooltipEl.classList.contains('mivent-tooltip--visible')) return;
  if (!activeTrigger.isConnected) {
    hideTooltip();
    return;
  }
  if (raf) cancelAnimationFrame(raf);
  raf = requestAnimationFrame(() => {
    raf = 0;
    const rect = activeTrigger.getBoundingClientRect();
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const tRect = tooltipEl.getBoundingClientRect();
    const placement = computePlacement(
      activeTrigger,
      rect,
      tRect.width,
      tRect.height,
      vw,
      vh
    );
    applyPlacement(tooltipEl, placement.pos, placement.left, placement.top);
  });
}

/* ---------------------------------------------------------------- */
/* init                                                              */
/* ---------------------------------------------------------------- */

export function initTooltips() {
  // Initial sweep
  scanRoot(document);

  // Capture-phase mouseover: migrate lazy titles (before the browser shows a
  // native tooltip ~300ms), then float the premium tooltip above the trigger.
  const onMouseOver = (e) => {
    const target = e.target instanceof Element ? e.target : null;
    if (!target) return;
    const titleHit = target.closest('[title]');
    if (titleHit) migrateTitle(titleHit);
    const trigger = target.closest('[data-tooltip]');
    if (trigger) scheduleShow(trigger, SHOW_DELAY_HOVER);
  };

  const onMouseOut = (e) => {
    const target = e.target instanceof Element ? e.target : null;
    const oldTrigger = target ? target.closest('[data-tooltip]') : null;
    if (!oldTrigger) return;
    const rel = e.relatedTarget instanceof Element ? e.relatedTarget.closest('[data-tooltip]') : null;
    if (rel === oldTrigger) return; // still inside the same trigger
    hideTooltip();
  };

  const onFocusIn = (e) => {
    const target = e.target instanceof Element ? e.target : null;
    const trigger = target ? target.closest('[data-tooltip]') : null;
    if (trigger) scheduleShow(trigger, SHOW_DELAY_FOCUS);
  };

  const onFocusOut = (e) => {
    const target = e.target instanceof Element ? e.target : null;
    const oldTrigger = target ? target.closest('[data-tooltip]') : null;
    if (!oldTrigger) return;
    const rel = e.relatedTarget instanceof Element ? e.relatedTarget.closest('[data-tooltip]') : null;
    if (rel === oldTrigger) return;
    hideTooltip();
  };

  const onVisibility = () => {
    if (document.hidden) hideTooltip();
  };

  // Browser zoom / window resize can change the rect<->offset mapping.
  const onResizeRecalibrate = () => {
    spaceCalib = null;
  };

  document.addEventListener('mouseover', onMouseOver, true);
  document.addEventListener('mouseout', onMouseOut, true);
  document.addEventListener('focusin', onFocusIn, true);
  document.addEventListener('focusout', onFocusOut, true);
  document.addEventListener('scroll', handleScroll, true);
  window.addEventListener('resize', handleScroll);
  window.addEventListener('resize', onResizeRecalibrate);
  window.addEventListener('blur', hideTooltip);
  document.addEventListener('visibilitychange', onVisibility);

  // MutationObserver for dynamically added nodes or title attribute changes
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === 'attributes' && m.attributeName === 'title') {
        migrateTitle(m.target);
      }
      if (m.type === 'childList') {
        // The hovered trigger may have been removed (route change, list
        // re-render) without a mouseout — drop the tooltip if so.
        if (activeTrigger && !activeTrigger.isConnected) hideTooltip();
        m.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.hasAttribute('title')) migrateTitle(node);
          if (node.querySelectorAll) {
            node.querySelectorAll('[title]').forEach(migrateTitle);
          }
        });
      }
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['title'],
  });

  // Periodic fallback scan for React portals / shadow edge cases (very cheap)
  let intervalId = setInterval(() => scanRoot(document), 2000);
  setTimeout(() => clearInterval(intervalId), 30000);

  // Expose for debugging/manual re-scan
  if (typeof window !== 'undefined') {
    window.__awsmivent_rescanTooltips = () => scanRoot(document);
  }

  return () => {
    observer.disconnect();
    clearInterval(intervalId);
    document.removeEventListener('mouseover', onMouseOver, true);
    document.removeEventListener('mouseout', onMouseOut, true);
    document.removeEventListener('focusin', onFocusIn, true);
    document.removeEventListener('focusout', onFocusOut, true);
    document.removeEventListener('scroll', handleScroll, true);
    window.removeEventListener('resize', handleScroll);
    window.removeEventListener('resize', onResizeRecalibrate);
    window.removeEventListener('blur', hideTooltip);
    document.removeEventListener('visibilitychange', onVisibility);
    if (showTimer) clearTimeout(showTimer);
    if (raf) cancelAnimationFrame(raf);
    if (tooltipEl && tooltipEl.parentNode) tooltipEl.parentNode.removeChild(tooltipEl);
    tooltipEl = null;
    activeTrigger = null;
  };
}

export default initTooltips;